/**
 * ProductCategoryPickerModal — Screen 22 of the SPEC's 3-modal product flow.
 *
 * 5-card category grid (Checking / Savings / Certificates / IRAs / Safe
 * Deposit Box). Click a card → on-pick handler routes to the
 * ProductTilesPickerModal with the selected categoryKey. Mirrors the Person
 * Add Wizard bucket picker pattern (pdw-1-bucket).
 *
 * Bill 2026-05-13 — per "category icon + prominent product title" direction,
 * tiles use the canonical PCB category icon (same icon for every product in
 * the category at the next step). Product differentiation lives in the
 * product name + chips at the tiles picker.
 *
 * Sources:
 *  - docs/mockups/canonical-walkthrough/SPEC.md Screen 22 (line 2104)
 *  - feedback_no_deferring_crap_20260511.md — picker must be live-wired
 */
import { useMemo, useState } from 'react';
import { ModalShell, Button } from '@/components/primitives';
import { productCategoryIconUrl } from '@/icons/flat-library';
import { useConfig } from '@/contexts/ConfigContext';
import { rolloutAllows, rolloutHidesDisabled, SETTING_DISABLED_DISPLAY, SETTING_ENABLED_ENTITY_GROUPS, SETTING_ENABLED_PRODUCT_CATEGORIES } from '../../../utils/rolloutScope';
import { enabledEntityTypes, productInRolloutScope } from '../rolloutProducts';

export type ProductCategoryKey = 'checking' | 'savings' | 'certificates' | 'iras' | 'sdb';

export interface ProductCategoryPickerModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (categoryKey: ProductCategoryKey) => void;
}

/**
 * Map AcctType (Core Director Field 23 numeric) → category key. Mirrors the
 * canonical 5 product categories (Bill 2026-05-11 SPEC lock — Phase 1):
 *   10 → checking
 *   20 → savings
 *   30 → certificates (CD)
 *   60 → iras
 *   50 → sdb
 */
function categoryForAcctType(acctType: string): ProductCategoryKey | undefined {
  switch (acctType) {
    case '10': return 'checking';
    case '20': return 'savings';
    case '30': return 'certificates';
    case '60': return 'iras';
    case '50': return 'sdb';
    default:   return undefined;
  }
}

interface CategoryCard {
  readonly key: ProductCategoryKey;
  readonly iconSlug: string; // matches PRODUCT_CATEGORY_ICONS key
  readonly title: string;
  readonly description: string;
  readonly footerNote?: string;
}

const CATEGORY_CARDS: ReadonlyArray<CategoryCard> = [
  {
    key: 'checking',
    iconSlug: 'dda',
    title: 'Checking',
    description: 'Everyday transaction accounts. Includes Free, Ultimate, Student, Senior, Business, NOW, and Public Funds variants.',
  },
  {
    key: 'savings',
    iconSlug: 'sav',
    title: 'Savings',
    description: 'Interest-bearing savings + money-market accounts. Includes Statement, Premier Money Market, Minor Savings, and Christmas Club.',
  },
  {
    key: 'certificates',
    iconSlug: 'cd',
    title: 'Certificates (CD)',
    description: 'Term deposits with fixed interest. 19 products at launch — varying terms, no-penalty options, jumbo tier.',
  },
  {
    key: 'iras',
    iconSlug: 'ira',
    title: 'IRAs',
    description: 'Tax-advantaged retirement accounts. Traditional, Roth, SEP, SIMPLE, Education, and Beneficiary variants.',
  },
  {
    key: 'sdb',
    iconSlug: 'sdb',
    title: 'Safe Deposit Box',
    description: 'Physical box assignment + annual rent. Phase 1 covers box assignment only; key-management workflow defers to Phase 1.5.',
    footerNote: 'Phase 1: box assignment only',
  },
];

export function ProductCategoryPickerModal({
  open,
  onClose,
  onPick,
}: ProductCategoryPickerModalProps): JSX.Element {
  const { config } = useConfig();
  const [picked, setPicked] = useState<ProductCategoryKey | null>(null);

  // Count active products per category for the card's product-count chip.
  const settings = config.systemSettings as Record<string, unknown> | undefined;
  const enabledTypes = useMemo(
    () => enabledEntityTypes(settings?.[SETTING_ENABLED_ENTITY_GROUPS]),
    [settings],
  );
  const countsByCategory = useMemo(() => {
    const counts = new Map<ProductCategoryKey, number>();
    for (const p of (config.products ?? [])) {
      if (!p.isActive) continue;
      if (!productInRolloutScope(p, enabledTypes)) continue; // out of this phase
      const key = categoryForAcctType(p.acctType);
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [config.products, enabledTypes]);

  // Phase rollout scope (Bill 2026-10-06): categories not in rollout.enabledProductCategories
  // show greyed "Coming soon" — Phase 1 opens Checking and Savings only.
  const enabledRaw = settings?.[SETTING_ENABLED_PRODUCT_CATEGORIES];
  const shownCards = rolloutHidesDisabled(settings?.[SETTING_DISABLED_DISPLAY])
    ? CATEGORY_CARDS.filter((c) => rolloutAllows(enabledRaw, c.key))
    : CATEGORY_CARDS;

  const totalProducts = useMemo(
    () => Array.from(countsByCategory.values()).reduce((a, b) => a + b, 0),
    [countsByCategory],
  );

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Add product · Choose a category"
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>← Back</Button>
      }
    >
      <div className="rao-cat-picker">
        <header className="rao-cat-picker__header">
          <h3 className="rao-cat-picker__title">What kind of product?</h3>
          <p className="rao-cat-picker__sub">
            Pick the category that fits this cart. The next step lists every
            product in that category — eligibility is calculated against the
            cart's applicants.
          </p>
        </header>

        <div className="rao-cat-picker__grid">
          {shownCards.map((card) => {
            const count = countsByCategory.get(card.key) ?? 0;
            const isPicked = picked === card.key;
            const disabled = !rolloutAllows(enabledRaw, card.key);
            const iconUrl = productCategoryIconUrl(card.iconSlug);
            return (
              <button
                key={card.key}
                type="button"
                className={`rao-cat-picker__card ${isPicked ? 'is-picked' : ''}`.trim()}
                aria-pressed={isPicked ? 'true' : 'false'}
                aria-disabled={disabled ? 'true' : 'false'}
                title={disabled ? `${card.title} is not yet available — coming soon.` : undefined}
                onClick={() => { if (disabled) return; setPicked(card.key); onPick(card.key); }}
                style={disabled ? { opacity: 0.5, filter: 'grayscale(1)', cursor: 'not-allowed', position: 'relative' } : undefined}
              >
                {disabled && (
                  <span
                    style={{
                      position: 'absolute', top: 10, right: 10, fontSize: 10, fontWeight: 700,
                      letterSpacing: 0.5, padding: '2px 8px', borderRadius: 10,
                      background: 'var(--ink-200, #e2e8f0)', color: 'var(--ink-600, #475569)',
                    }}
                  >
                    COMING SOON
                  </span>
                )}
                <div className="rao-cat-picker__icon">
                  {iconUrl && <img src={iconUrl} alt="" aria-hidden width={64} height={64} />}
                </div>
                <div className="rao-cat-picker__name">{card.title}</div>
                <div className="rao-cat-picker__count">
                  {count} {count === 1 ? 'product' : 'products'}
                </div>
                <div className="rao-cat-picker__desc">{card.description}</div>
                {card.footerNote && (
                  <div className="rao-cat-picker__note">{card.footerNote}</div>
                )}
              </button>
            );
          })}
        </div>

        <p className="rao-cat-picker__footer">
          {shownCards.length} {shownCards.length === 1 ? 'category' : 'categories'} · {totalProducts} products available.
          Eligibility per cart applicant set is calculated at the next step.
        </p>
      </div>
    </ModalShell>
  );
}
